import type { TickCtx, GameState } from '../tickTypes'
import { Dec } from '../../shared/decimal'
import { knowledgeUpgrade2EffectValue } from '../formulas'

// script.js:1577-1609 (uranium) + :1613-1703 (cyan/blue/indigo/violet/pink
// sigils) — state half only; every document.* / format() line skipped.
export function uraniumEarlySigils(game: GameState, ctx: TickCtx): void {
  void ctx

  // script.js:1577-1609 — uraniumToGet + bestUraniumToGet high-water
  if (game.unlocks >= 7) {
    // script.js:1580-1584
    game.uraniumToGet = game.platinum.add(1).log2().mul(1 + 0.1 * game.uraniumUpgradesBought[1])
    if (game.unlocks >= 8) game.uraniumToGet = game.uraniumToGet.mul(2 ** game.platinumUpgradesBought[7])
    if (game.darkMagicUpgradesBought[4]) game.uraniumToGet = game.uraniumToGet.mul(100)
    if (game.unlocks >= 10) game.uraniumToGet = game.uraniumToGet.mul(game.cyanSigilUpgradesBought[1].add(1).pow(1.5))
    if (game.holyTetrahedronUpgradesBought[5]) game.uraniumToGet = game.uraniumToGet.mul(100)
    // script.js:1585-1590 — the oracle inlines knowledgeUpgrade2Effect here
    // (with both clamps); canonical formula call instead.
    if (game.unlocks >= 15) {
      const knowledgeUpgrade2Effect = knowledgeUpgrade2EffectValue(game)
      game.uraniumToGet = game.uraniumToGet.pow(knowledgeUpgrade2Effect)
    }
    // script.js:1591
    if (game.voidMagicUpgradesBought[12] == true) game.uraniumToGet = game.uraniumToGet.pow(1e30)
    // script.js:1592-1598 — QUIRK: this guard requires deathEssenceUpgradesBought[2].gt(0)
    // (unlike the display copy elsewhere); transcribed exactly as written here.
    if (game.unlocks >= 34 && game.deathEssenceUpgradesBought[2].gt(0)) {
      let deathEssenceUpgrade3Effect = new Dec(2).pow(game.deathEssenceUpgradesBought[2].pow(0.7)).mul(10000)
      if (deathEssenceUpgrade3Effect.gt(500000)) deathEssenceUpgrade3Effect = deathEssenceUpgrade3Effect.mul(500000).pow(0.5)
      if (deathEssenceUpgrade3Effect.gt(1e9)) deathEssenceUpgrade3Effect = new Dec(1e9)
      deathEssenceUpgrade3Effect = new Dec(10).pow(deathEssenceUpgrade3Effect)
      game.uraniumToGet = game.uraniumToGet.pow(deathEssenceUpgrade3Effect)
    }
    // script.js:1599-1604 — hell layer penalties
    if (game.inHell) {
      if (game.currentHellLayer == 1) game.uraniumToGet = game.uraniumToGet.pow(0.1)
      else if (game.currentHellLayer == 2) game.uraniumToGet = game.uraniumToGet.pow(0.01)
      else if (game.currentHellLayer == 3 || game.currentHellLayer == 4) game.uraniumToGet = game.uraniumToGet.add(1).log10()
      else if (game.currentHellLayer == 5) game.uraniumToGet = game.uraniumToGet.add(1).log10().pow(0.5)
    }
    // script.js:1605
    game.uraniumToGet = game.uraniumToGet.floor()
    // script.js:1607 — high-water mark
    if (game.uraniumToGet.gt(game.bestUraniumToGet)) game.bestUraniumToGet = game.uraniumToGet
  }

  // script.js:1613-1633 — cyan sigils
  if (game.unlocks >= 10) {
    game.cyanSigilsToGet = game.gold.add(1).log10().div(new Dec(3000).sub(game.blueSigilUpgradesBought[2].mul(100))).floor()
    if (game.darkMagicUpgradesBought[8] == true) game.cyanSigilsToGet = game.cyanSigilsToGet.mul(3)
    game.cyanSigilsToGet = game.cyanSigilsToGet.mul(new Dec(5).pow(game.dragonPets ** 0.5))
    if (game.unlocks >= 15) game.cyanSigilsToGet = game.cyanSigilsToGet.mul(game.highestKnowledge.div(3).pow(0.7).add(1))
    if (game.unlocks >= 18) game.cyanSigilsToGet = game.cyanSigilsToGet.mul(game.blood.pow(2).add(1))
    if (game.plutoniumUpgradesBought[2] > 0) game.cyanSigilsToGet = game.cyanSigilsToGet.mul(game.plutonium.add(1).pow(0.5).mul(10).pow(game.plutoniumUpgradesBought[2]))
    if (game.holyTetrahedronUpgradesBought[2]) game.cyanSigilsToGet = game.cyanSigilsToGet.mul(1e50)
    if (game.unlocks >= 34 && game.deathEssenceUpgradesBought[1].gt(0)) game.cyanSigilsToGet = game.cyanSigilsToGet.mul(new Dec(10).pow(new Dec(2).pow(game.deathEssenceUpgradesBought[1].pow(0.5)).mul(1e10)))
    game.cyanSigilsToGet = game.cyanSigilsToGet.round()
    // script.js:1628-1630
    game.cyanSigilPowerPerSecond = game.cyanSigils.pow(2).div(100).mul(game.cyanSigilUpgradesBought[0].add(1))
    if (game.unlocks >= 12) game.cyanSigilPowerPerSecond = game.cyanSigilPowerPerSecond.pow(Dec.min(new Dec(1.15).pow(game.indigoSigilUpgradesBought[1].pow(0.5)), 30))
    if (game.voidMagicUpgradesBought[2]) game.cyanSigilPowerPerSecond = game.cyanSigilPowerPerSecond.pow(2)
  }

  // script.js:1634-1651 — blue sigils
  if (game.unlocks >= 11) {
    game.blueSigilsToGet = game.gold.add(1).log10().div(8000).floor()
    if (game.darkMagicUpgradesBought[8] == true) game.blueSigilsToGet = game.blueSigilsToGet.mul(3)
    game.blueSigilsToGet = game.blueSigilsToGet.mul(new Dec(5).pow(game.dragonPets ** 0.5))
    if (game.unlocks >= 15) game.blueSigilsToGet = game.blueSigilsToGet.mul(game.highestKnowledge.div(3).pow(0.7).add(1))
    if (game.unlocks >= 18) game.blueSigilsToGet = game.blueSigilsToGet.mul(game.blood.pow(2).add(1))
    if (game.plutoniumUpgradesBought[2] > 0) game.blueSigilsToGet = game.blueSigilsToGet.mul(game.plutonium.add(1).pow(0.5).mul(10).pow(game.plutoniumUpgradesBought[2]))
    game.blueSigilsToGet = game.blueSigilsToGet.round()
    // script.js:1646-1647
    game.blueSigilPowerPerSecond = game.blueSigils.pow(2).div(100).mul(game.blueSigilUpgradesBought[0].add(1))
    if (game.unlocks >= 13) game.blueSigilPowerPerSecond = game.blueSigilPowerPerSecond.pow(new Dec(1.3).pow(game.violetSigilUpgradesBought[1].pow(0.5)))
  }

  // script.js:1652-1668 — indigo sigils
  if (game.unlocks >= 12) {
    game.indigoSigilsToGet = game.gold.add(1).log10().div(16000).floor()
    game.indigoSigilsToGet = game.indigoSigilsToGet.mul(new Dec(5).pow(game.dragonPets ** 0.5))
    if (game.unlocks >= 15) game.indigoSigilsToGet = game.indigoSigilsToGet.mul(game.highestKnowledge.div(3).pow(0.7).add(1))
    if (game.unlocks >= 18) game.indigoSigilsToGet = game.indigoSigilsToGet.mul(game.blood.pow(2).add(1))
    if (game.plutoniumUpgradesBought[2] > 0) game.indigoSigilsToGet = game.indigoSigilsToGet.mul(game.plutonium.add(1).pow(0.5).mul(10).pow(game.plutoniumUpgradesBought[2]))
    game.indigoSigilsToGet = game.indigoSigilsToGet.round()
    // script.js:1663-1664
    game.indigoSigilPowerPerSecond = game.indigoSigils.pow(2).div(100).mul(game.indigoSigilUpgradesBought[0].add(1))
    if (game.tomeUpgradesBought[0]) game.indigoSigilPowerPerSecond = game.indigoSigilPowerPerSecond.pow(1.3)
  }

  // script.js:1669-1684 — violet sigils
  if (game.unlocks >= 13) {
    game.violetSigilsToGet = game.gold.add(1).log10().div(30000).floor()
    game.violetSigilsToGet = game.violetSigilsToGet.mul(new Dec(5).pow(game.dragonPets ** 0.5))
    if (game.unlocks >= 15) game.violetSigilsToGet = game.violetSigilsToGet.mul(game.highestKnowledge.div(3).pow(0.7).add(1))
    if (game.unlocks >= 18) game.violetSigilsToGet = game.violetSigilsToGet.mul(game.blood.pow(2).add(1))
    if (game.plutoniumUpgradesBought[2] > 0) game.violetSigilsToGet = game.violetSigilsToGet.mul(game.plutonium.add(1).pow(0.5).mul(10).pow(game.plutoniumUpgradesBought[2]))
    game.violetSigilsToGet = game.violetSigilsToGet.round()
    // script.js:1681
    game.violetSigilPowerPerSecond = game.violetSigils.pow(2).div(100).mul(game.violetSigilUpgradesBought[0].add(1))
  }

  // script.js:1685-1703 — pink sigils
  if (game.unlocks >= 14) {
    game.pinkSigilsToGet = game.gold.add(1).log10().div(300000).floor()
    game.pinkSigilsToGet = game.pinkSigilsToGet.mul(new Dec(5).pow(game.dragonPets ** 0.5))
    if (game.unlocks >= 15) game.pinkSigilsToGet = game.pinkSigilsToGet.mul(game.highestKnowledge.div(3).pow(0.7).add(1))
    if (game.unlocks >= 18) game.pinkSigilsToGet = game.pinkSigilsToGet.mul(game.blood.pow(2).add(1))
    if (game.plutoniumUpgradesBought[2] > 0) game.pinkSigilsToGet = game.pinkSigilsToGet.mul(game.plutonium.add(1).pow(0.5).mul(10).pow(game.plutoniumUpgradesBought[2]))
    if (game.holyTetrahedronUpgradesBought[8]) game.pinkSigilsToGet = game.pinkSigilsToGet.pow(2)
    if (game.unlocks >= 33) game.pinkSigilsToGet = game.pinkSigilsToGet.pow(new Dec(1.1).pow(game.lightEssenceUpgradesBought[2].pow(0.5)))
    game.pinkSigilsToGet = game.pinkSigilsToGet.round()
    // script.js:1699-1700
    game.pinkSigilPowerPerSecond = game.pinkSigils.pow(2).div(100).mul(game.pinkSigilUpgradesBought[0].add(1))
    if (game.tomeUpgradesBought[1]) game.pinkSigilPowerPerSecond = game.pinkSigilPowerPerSecond.mul(game.totalTomes.pow(0.5).mul(3).add(1))
  }
}
